import { useState, useRef, type KeyboardEvent } from 'react';
import { motion } from 'motion/react';
import { Send, Square, SquarePen } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';

interface ComposerProps {
  onSend: (text: string) => void;
  isStreaming: boolean;
  onStop: () => void;
  /** Renders a small "clear chat" icon button on the left, where the old +/mic buttons sat. Omit to hide it. */
  onClear?: () => void;
  /** Sitting in the middle of an empty chat (no bottom safe-area padding). */
  centered?: boolean;
}

export default function Composer({ onSend, isStreaming, onStop, onClear, centered }: ComposerProps) {
  const c = useAppColors();
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleSend() {
    const trimmed = value.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setValue(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  return (
    <div
      className="flex-shrink-0 px-4 pb-4 pt-2"
      style={{ background: centered ? 'transparent' : c.bg, paddingBottom: centered ? '0.25rem' : 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <div
        className="max-w-3xl mx-auto flex items-end gap-2 rounded-3xl px-5 py-3.5 transition-colors"
        style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}
      >
        {onClear && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClear}
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: c.iconBg, color: c.iconColor }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = c.text; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = c.iconColor; }}
            aria-label="New chat"
            title="New chat (this one stays saved)"
          >
            <SquarePen size={15} />
          </motion.button>
        )}

        <textarea
          data-tour="composer"
          ref={textareaRef}
          value={value}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="Message OpsAlo..."
          className="flex-1 bg-transparent resize-none outline-none text-[15px] leading-6 max-h-40"
          style={{ color: c.text }}
        />

        {isStreaming ? (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onStop}
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}`, color: c.accentSoft }}
            aria-label="Stop generating"
          >
            <Square size={14} fill="currentColor" />
          </motion.button>
        ) : (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleSend}
            disabled={!value.trim()}
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            style={{ background: c.accent, color: 'white' }}
            aria-label="Send message"
          >
            <Send size={15} />
          </motion.button>
        )}
      </div>
      <p className="max-w-3xl mx-auto text-center text-[11px] mt-2" style={{ color: c.textFaint }}>
        OpsAlo is powered by AI and can make mistakes. Double-check anything important.
      </p>
    </div>
  );
}
